import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { BookOpen, Boxes, ChevronDown, Layers, Menu, MessageSquare, Milestone, X } from 'lucide-react'
import { BrandLockup } from '@/components/BrandLogo'
import { ThemeToggle } from '@/components/ThemeToggle'
import { SuiteLinks, SuiteSwitcher } from '@/components/landing/SuiteSwitcher'
import { isDesktopApp, SIGNED_IN_HOME } from '@/services/platform'
import { useAuth } from '@/store/auth'

/**
 * The masthead shared by every marketing page.
 *
 * <p>Each entry is a page of its own rather than an anchor into one long
 * scroll, so a visitor can link to, bookmark and go back to any one of them.
 * The four pages that describe how the product works sit under one "About"
 * menu, the way the rest of the suite groups its own — a masthead of six flat
 * links reads as a sitemap rather than as navigation.
 */
export const ABOUT = [
  {
    to: '/model-hub',
    label: 'Model hub',
    note: 'Find a model the way you find an app',
    icon: <Boxes size={17} />,
  },
  {
    to: '/knowledge',
    label: 'Knowledge',
    note: 'Ask questions of your own documents',
    icon: <BookOpen size={17} />,
  },
  {
    to: '/workspace',
    label: 'Workspace',
    note: 'One window, whichever model answers',
    icon: <MessageSquare size={17} />,
  },
  {
    to: '/roadmap',
    label: 'Roadmap',
    note: 'What exists now, and what is ahead',
    icon: <Milestone size={17} />,
  },
]

/** A section heading inside the mobile menu. */
function MenuLabel({ children }: { children: string }) {
  return (
    <p className="px-2 pb-1 pt-4 text-[11px] font-extrabold uppercase tracking-[0.08em] text-brand-muted">
      {children}
    </p>
  )
}

/**
 * One row of the mobile menu: an icon tile, the name, and the line that says
 * what the page is for. A full-screen menu has room to explain itself, and a
 * column of bare words does not.
 */
function MenuItem({
  to,
  icon,
  label,
  note,
}: {
  to: string
  icon: ReactNode
  label: string
  note: string
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex gap-3 rounded-xl px-2 py-2.5 transition ${
          isActive ? 'bg-brand-tint/50' : 'active:bg-brand-tint/40'
        }`
      }
    >
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-tint text-brand-tint-ink">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-bold text-brand-heading">{label}</span>
        <span className="mt-0.5 block text-[12px] leading-snug text-brand-muted">{note}</span>
      </span>
    </NavLink>
  )
}

export function SiteHeader() {
  const [open, setOpen] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  const about = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()

  // These pages are reachable while signed in, so the masthead must not go on
  // offering to sign the reader in. The slot becomes the way back to the work.
  const authed = useAuth((s) => s.status === 'authenticated')
  // "Open" is the right word on the site, where the app is somewhere else to
  // go. Inside the app it is already open, and the link is simply the way back
  // out of a marketing page -- which these are, and which the app can reach.
  const account = authed
    ? {
        to: SIGNED_IN_HOME,
        label: isDesktopApp ? 'Back to NevTan AI' : 'Open NevTan AI',
      }
    : { to: '/login', label: 'Sign in' }

  // A tap that navigates should also put both menus away.
  useEffect(() => {
    setOpen(false)
    setAboutOpen(false)
  }, [pathname])

  // Clicking anywhere else, or pressing Escape, closes the menu — otherwise it
  // sits open over the page after the pointer has moved on.
  useEffect(() => {
    if (!aboutOpen) return
    const onClick = (e: MouseEvent) => {
      if (!about.current?.contains(e.target as Node)) setAboutOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAboutOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [aboutOpen])

  const aboutActive = ABOUT.some((item) => item.to === pathname)
  const linkClass = (isActive: boolean) =>
    `rounded-lg px-2.5 py-1.5 text-[14px] font-bold transition ${
      isActive
        ? 'bg-brand-tint/60 text-brand-tint-ink'
        : 'text-brand-body hover:bg-brand-tint/50 hover:text-brand'
    }`

  return (
    // Translucent white over a 10px blur on a hairline rule, exactly as the
    // rest of the suite sets its masthead.
    <header className="sticky top-0 z-30 border-b border-brand-line bg-brand-paper/85 backdrop-blur-[10px]">
      <div className="flex h-[65px] items-center gap-4 px-5 sm:px-8 lg:px-12">
        <Link to="/" className="flex shrink-0 items-center" aria-label="NevTan AI, home">
          <BrandLockup tone="teal" markClassName="h-8 w-8 sm:h-9 sm:w-9" textClassName="text-[19px] sm:text-[21px]" />
        </Link>

        <nav className="ml-auto hidden items-center gap-1 lg:flex">
          <SuiteSwitcher />
          <NavLink to="/features" className={({ isActive }) => linkClass(isActive)}>
            Features
          </NavLink>

          <div ref={about} className="relative">
            <button
              type="button"
              onClick={() => setAboutOpen((v) => !v)}
              aria-expanded={aboutOpen}
              aria-haspopup="true"
              className={`${linkClass(aboutActive)} inline-flex items-center gap-1`}
            >
              About
              <ChevronDown
                size={14}
                className={`transition-transform ${aboutOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {aboutOpen && (
              <div className="absolute right-0 top-full z-40 mt-1.5 w-[290px] overflow-hidden rounded-xl border border-brand-line bg-brand-paper p-1.5 shadow-float">
                {ABOUT.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `block rounded-lg px-3 py-2 transition ${
                        isActive ? 'bg-brand-tint/60' : 'hover:bg-brand-tint/40'
                      }`
                    }
                  >
                    <span className="block text-[13.5px] font-bold text-brand-heading">
                      {item.label}
                    </span>
                    <span className="mt-0.5 block text-[12px] leading-snug text-brand-muted">
                      {item.note}
                    </span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="ml-auto flex items-center gap-1.5 lg:ml-2">
          <ThemeToggle />
          <Link
            to={account.to}
            className="hidden whitespace-nowrap rounded-lg px-4 py-1.5 text-[14px] font-bold text-brand transition hover:bg-brand-tint/50 sm:block"
          >
            {account.label}
          </Link>
          <Link to="/download" className="nv-btn-primary">
            Download
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            className="rounded-lg p-1.5 text-brand-body lg:hidden"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        // Capped and scrollable: with the suite listed out, the menu is taller
        // than a phone and the actions at the bottom must stay reachable.
        <nav className="max-h-[calc(100vh-65px)] overflow-y-auto border-t border-brand-line bg-brand-paper px-3 pb-4 lg:hidden">
          <div className="pt-2">
            <MenuItem
              to="/features"
              icon={<Layers size={17} />}
              label="Features"
              note="Every kind of model, in one place"
            />
          </div>

          {/* No nested disclosure on a phone: the four are listed under a
              heading, which is fewer taps than a menu inside a menu. */}
          <MenuLabel>About</MenuLabel>
          {ABOUT.map((item) => (
            <MenuItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              note={item.note}
            />
          ))}

          <MenuLabel>NevTan suite</MenuLabel>
          <SuiteLinks />

          <div className="mt-4 flex gap-2 border-t border-brand-line pt-4">
            <Link
              to={account.to}
              className="flex-1 rounded-lg border border-brand-line-strong px-4 py-2.5 text-center text-[14px] font-bold text-brand-body transition active:border-brand active:text-brand"
            >
              {account.label}
            </Link>
            <Link
              to="/download"
              className="flex-1 rounded-lg bg-brand px-4 py-2.5 text-center text-[14px] font-bold text-white shadow-brand"
            >
              Download
            </Link>
          </div>
        </nav>
      )}
    </header>
  )
}
